import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  DragIndicator as DragIndicatorIcon,
  Home as HomeIcon,
  MoreVert as MoreVertIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material'
import { ROUTES } from '@/utils/constants'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { EmojiField } from '@/components/common/EmojiField'
import { adminChatStickersApi } from '@/api/adminChatStickers'
import type { ChatSticker, ChatStickerPack } from '@/api/chat'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { prepareStickerImage } from '@/utils/stickerImage'
import '@/styles/dashboard.css'

// Конвертер приводит всё к WebP/PNG сам — на вход можно и JPEG.
const STICKER_ACCEPT = 'image/webp,image/png,image/jpeg'

/**
 * Стикеры одного набора: таблица с превью и inline-переключением активности,
 * добавление и редактирование — модалками (emoji выбирается picker'ом как в чате).
 */
export function AdminChatStickerPackPage() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const params = useParams<{ packId: string }>()
  const packId = Number(params.packId)

  const [pack, setPack] = useState<ChatStickerPack | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Локальный порядок для drag-and-drop: во время перетаскивания список
  // переупорядочивается на лету, сохранение — одним запросом на dragend.
  const [stickerList, setStickerList] = useState<ChatSticker[]>([])
  const [dragId, setDragId] = useState<number | null>(null)

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuSticker, setMenuSticker] = useState<ChatSticker | null>(null)

  // Модалка добавления
  const [uploadOpen, setUploadOpen] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadPreview, setUploadPreview] = useState<string | null>(null)
  const [uploadCode, setUploadCode] = useState('')
  const [uploadEmoji, setUploadEmoji] = useState('')
  const [uploading, setUploading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  // Модалка редактирования
  const [editSticker, setEditSticker] = useState<ChatSticker | null>(null)
  const [editCode, setEditCode] = useState('')
  const [editEmoji, setEditEmoji] = useState('')
  const [saving, setSaving] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  // Снимок порядка на dragstart (для отмены по Esc / drop мимо таблицы)
  // и флаг фактического drop — dragend без drop сохранять не должен.
  const dragStartOrderRef = useRef<ChatSticker[] | null>(null)
  const droppedRef = useRef(false)

  const showError = (err: unknown) =>
    setError(displayErrorMessage(getErrorMessage(err), t('common.error')))

  const loadPack = async () => {
    setError(null)
    try {
      const packs = await adminChatStickersApi.getPacks()
      setPack(packs.find((p) => p.id === packId) ?? null)
    } catch (err) {
      showError(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!Number.isFinite(packId)) {
      navigate(ROUTES.ADMIN_CHAT_STICKERS, { replace: true })
      return
    }
    void loadPack()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packId])

  useEffect(() => {
    // Не сбрасывать локальный порядок посреди перетаскивания (например,
    // если параллельный запрос обновил pack).
    if (dragId !== null) return
    setStickerList(pack?.stickers ?? [])
  }, [pack, dragId])

  const saveOrder = async (ids: number[]) => {
    if (!pack) return
    setError(null)
    try {
      const updated = await adminChatStickersApi.reorderStickers(pack.id, ids)
      setPack(updated)
      setSuccess(t('admin.chatStickers.orderSaved'))
    } catch (err) {
      // loadPack() начинается с setError(null) — сообщение ставим ПОСЛЕ
      // перезагрузки, иначе админ видит молчаливый откат порядка.
      const message = displayErrorMessage(getErrorMessage(err), t('common.error'))
      await loadPack()
      setError(message)
    }
  }

  const handleDragStart = (id: number) => (event: React.DragEvent) => {
    setDragId(id)
    droppedRef.current = false
    dragStartOrderRef.current = stickerList
    event.dataTransfer.effectAllowed = 'move'
    // Firefox не начинает HTML5-drag без хотя бы одного setData.
    event.dataTransfer.setData('text/plain', String(id))
  }

  const handleDragOver = (id: number) => (event: React.DragEvent) => {
    event.preventDefault()
    if (dragId === null || dragId === id) return
    setStickerList((prev) => {
      const from = prev.findIndex((s) => s.id === dragId)
      const to = prev.findIndex((s) => s.id === id)
      if (from < 0 || to < 0 || from === to) return prev
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next
    })
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    droppedRef.current = true
  }

  const handleDragEnd = async () => {
    setDragId(null)
    const startOrder = dragStartOrderRef.current
    dragStartOrderRef.current = null

    // Esc или отпускание мимо таблицы — отмена перетаскивания, не сохранение.
    if (!droppedRef.current) {
      if (startOrder !== null) setStickerList(startOrder)
      return
    }

    if (!pack) return
    const ids = stickerList.map((s) => s.id)
    const original = (pack.stickers ?? []).map((s) => s.id)
    if (ids.join(',') === original.join(',')) return
    await saveOrder(ids)
  }

  // Перестановка из меню строки — путь для touch-устройств и клавиатуры,
  // где HTML5 drag-and-drop недоступен.
  const moveSticker = async (sticker: ChatSticker, delta: -1 | 1) => {
    const index = stickerList.findIndex((s) => s.id === sticker.id)
    const target = index + delta
    if (index < 0 || target < 0 || target >= stickerList.length) return
    const next = [...stickerList]
    ;[next[index], next[target]] = [next[target], next[index]]
    setStickerList(next)
    await saveOrder(next.map((s) => s.id))
  }

  const openUploadDialog = () => {
    setUploadFile(null)
    setUploadPreview(null)
    setUploadCode('')
    setUploadEmoji('')
    setUploadError(null)
    setUploadOpen(true)
  }

  const handleUploadFilePick = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setUploadError(null)
    setProcessing(true)
    try {
      // Конвертация в браузере: обрезка прозрачных полей, вписывание
      // в 512×512, WebP под лимит 256 КБ (PNG-fallback для Safari).
      const prepared = await prepareStickerImage(file)
      setUploadFile(prepared.file)
      // Код прикидываем из имени исходного файла — админ может поправить
      const base = file.name.replace(/\.[^.]+$/, '')
      setUploadCode(base.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, ''))
      const reader = new FileReader()
      reader.onload = () => setUploadPreview(typeof reader.result === 'string' ? reader.result : null)
      reader.readAsDataURL(prepared.file)
    } catch {
      setUploadFile(null)
      setUploadPreview(null)
      setUploadError(t('admin.chatStickers.convertFailed'))
    } finally {
      setProcessing(false)
    }
  }

  const handleUpload = async () => {
    if (!pack || !uploadFile || uploadCode.trim() === '') {
      setUploadError(t('admin.chatStickers.validationRequired'))
      return
    }
    setUploading(true)
    setUploadError(null)
    try {
      await adminChatStickersApi.uploadSticker(pack.id, uploadFile, {
        code: uploadCode.trim(),
        emoji: uploadEmoji || undefined,
      })
      setUploadOpen(false)
      setSuccess(t('admin.chatStickers.stickerAdded'))
      await loadPack()
    } catch (err) {
      setUploadError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      setUploading(false)
    }
  }

  const openEditDialog = (sticker: ChatSticker) => {
    setEditSticker(sticker)
    setEditCode(sticker.code)
    setEditEmoji(sticker.emoji ?? '')
    setEditError(null)
  }

  const handleSaveEdit = async () => {
    if (!editSticker || editCode.trim() === '') {
      setEditError(t('admin.chatStickers.validationRequired'))
      return
    }
    setSaving(true)
    setEditError(null)
    try {
      await adminChatStickersApi.updateSticker(editSticker.id, {
        code: editCode.trim(),
        emoji: editEmoji || null,
      })
      setEditSticker(null)
      setSuccess(t('admin.chatStickers.stickerSaved'))
      await loadPack()
    } catch (err) {
      setEditError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      setSaving(false)
    }
  }

  const handleToggleSticker = async (sticker: ChatSticker) => {
    setError(null)
    try {
      await adminChatStickersApi.updateSticker(sticker.id, { is_active: !sticker.is_active })
      await loadPack()
    } catch (err) {
      showError(err)
    }
  }

  const handleDeleteSticker = async (sticker: ChatSticker) => {
    if (!window.confirm(t('admin.chatStickers.deleteStickerConfirm', { code: sticker.code }))) return
    setError(null)
    try {
      await adminChatStickersApi.deleteSticker(sticker.id)
      setSuccess(t('admin.chatStickers.stickerDeleted'))
      await loadPack()
    } catch (err) {
      showError(err)
    }
  }

  const openMenu = (event: React.MouseEvent<HTMLElement>, sticker: ChatSticker) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
    setMenuSticker(sticker)
  }

  const closeMenu = () => setMenuAnchor(null)

  const stickers = stickerList

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <Link
            to={ROUTES.DASHBOARD}
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Typography color="text.primary">{t('dashboard.administration')}</Typography>
          <Link to={ROUTES.ADMIN_CHAT_STICKERS} style={{ textDecoration: 'none', color: 'inherit' }}>
            {t('admin.chatStickers.title')}
          </Link>
          <Typography color="text.primary">{pack?.title ?? '…'}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {pack?.title ?? t('admin.chatStickers.title')}
          </Typography>
          <PageHelpPopover pageKey="admin-chat-sticker-pack" />
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            {t('admin.chatStickers.fileHint')}
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={openUploadDialog}
              disabled={!pack}
              sx={{ height: 40 }}
            >
              {t('admin.chatStickers.addSticker')}
            </Button>
          </Box>
        </Box>

        {loading ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>{t('admin.chatStickers.columnPreview')}</TableCell>
                  <TableCell>{t('admin.chatStickers.columnCode')}</TableCell>
                  <TableCell>{t('admin.chatStickers.columnEmoji')}</TableCell>
                  <TableCell>{t('admin.chatStickers.columnStatus')}</TableCell>
                  <TableCell align="right">{t('admin.users.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2, 3].map((i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton variant="text" width={16} /></TableCell>
                    <TableCell><Skeleton variant="rounded" width={48} height={48} /></TableCell>
                    <TableCell><Skeleton variant="text" width={120} /></TableCell>
                    <TableCell><Skeleton variant="text" width={40} /></TableCell>
                    <TableCell><Skeleton variant="rectangular" width={40} height={24} sx={{ borderRadius: 3 }} /></TableCell>
                    <TableCell align="right"><Skeleton variant="circular" width={32} height={32} sx={{ ml: 'auto' }} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : !pack ? (
          <Paper sx={{ p: 3 }}>
            <Typography color="text.secondary">{t('admin.chatStickers.packNotFound')}</Typography>
          </Paper>
        ) : stickers.length === 0 ? (
          <Paper sx={{ p: 3 }}>
            <Typography color="text.secondary">{t('admin.chatStickers.noStickers')}</Typography>
          </Paper>
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {stickers.map((sticker) => (
              <Paper key={sticker.id} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <img
                    src={sticker.url}
                    alt={sticker.code}
                    loading="lazy"
                    style={{ width: 48, height: 48, objectFit: 'contain', opacity: sticker.is_active ? 1 : 0.4 }}
                  />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography fontWeight={600} noWrap sx={{ fontFamily: 'monospace' }}>
                      {sticker.code}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {sticker.emoji && <Typography>{sticker.emoji}</Typography>}
                      <Chip
                        size="small"
                        color={sticker.is_active ? 'success' : 'default'}
                        label={
                          sticker.is_active
                            ? t('admin.chatStickers.statusActive')
                            : t('admin.chatStickers.statusInactive')
                        }
                      />
                    </Box>
                  </Box>
                  <IconButton size="small" onClick={(e) => openMenu(e, sticker)}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>{t('admin.chatStickers.columnPreview')}</TableCell>
                  <TableCell>{t('admin.chatStickers.columnCode')}</TableCell>
                  <TableCell>{t('admin.chatStickers.columnEmoji')}</TableCell>
                  <TableCell>{t('admin.chatStickers.columnStatus')}</TableCell>
                  <TableCell align="right">{t('admin.users.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stickers.map((sticker) => (
                  <TableRow
                    key={sticker.id}
                    draggable
                    onDragStart={handleDragStart(sticker.id)}
                    onDragOver={handleDragOver(sticker.id)}
                    onDrop={handleDrop}
                    onDragEnd={() => void handleDragEnd()}
                    sx={{ cursor: 'grab', opacity: sticker.id === dragId ? 0.5 : 1 }}
                  >
                    <TableCell sx={{ color: 'text.disabled' }}>
                      <DragIndicatorIcon fontSize="small" />
                    </TableCell>
                    <TableCell>
                      <img
                        src={sticker.url}
                        alt={sticker.code}
                        loading="lazy"
                        style={{ width: 48, height: 48, objectFit: 'contain', opacity: sticker.is_active ? 1 : 0.4 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontFamily: 'monospace' }}>{sticker.code}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '1.25rem' }}>{sticker.emoji ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={sticker.is_active ? 'success' : 'default'}
                        label={
                          sticker.is_active
                            ? t('admin.chatStickers.statusActive')
                            : t('admin.chatStickers.statusInactive')
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => openMenu(e, sticker)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          TransitionProps={{ onExited: () => setMenuSticker(null) }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              if (menuSticker) openEditDialog(menuSticker)
              closeMenu()
            }}
          >
            {t('admin.chatStickers.menuEdit')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuSticker) void handleToggleSticker(menuSticker)
              closeMenu()
            }}
          >
            {menuSticker?.is_active
              ? t('admin.chatStickers.menuDeactivate')
              : t('admin.chatStickers.menuActivate')}
          </MenuItem>
          <MenuItem
            disabled={!menuSticker || stickerList.findIndex((s) => s.id === menuSticker.id) <= 0}
            onClick={() => {
              if (menuSticker) void moveSticker(menuSticker, -1)
              closeMenu()
            }}
          >
            {t('admin.chatStickers.menuMoveUp')}
          </MenuItem>
          <MenuItem
            disabled={
              !menuSticker ||
              stickerList.findIndex((s) => s.id === menuSticker.id) >= stickerList.length - 1
            }
            onClick={() => {
              if (menuSticker) void moveSticker(menuSticker, 1)
              closeMenu()
            }}
          >
            {t('admin.chatStickers.menuMoveDown')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuSticker) void handleDeleteSticker(menuSticker)
              closeMenu()
            }}
          >
            {t('admin.chatStickers.menuDelete')}
          </MenuItem>
        </Menu>

        <Dialog open={uploadOpen} onClose={() => !uploading && setUploadOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{t('admin.chatStickers.uploadTitle')}</DialogTitle>
          <DialogContent>
            {uploadError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
                {uploadError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                {uploadPreview && (
                  <Box
                    component="img"
                    src={uploadPreview}
                    alt={uploadCode}
                    sx={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 1, border: 1, borderColor: 'divider' }}
                  />
                )}
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  disabled={processing}
                  startIcon={processing ? <CircularProgress size={16} /> : undefined}
                  sx={{ textTransform: 'none', height: 40 }}
                >
                  {processing
                    ? t('admin.chatStickers.processingImage')
                    : uploadFile
                      ? t('common.imageUpload.replace')
                      : t('admin.chatStickers.chooseFile')}
                  <input type="file" accept={STICKER_ACCEPT} hidden onChange={(e) => void handleUploadFilePick(e)} />
                </Button>
                {uploadFile && !processing && (
                  <Typography variant="caption" color="text.secondary">
                    {t('admin.chatStickers.resultSize', {
                      kb: Math.max(1, Math.round(uploadFile.size / 1024)),
                    })}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField
                  label={t('admin.chatStickers.fieldStickerCode')}
                  value={uploadCode}
                  onChange={(e) => setUploadCode(e.target.value)}
                  size="small"
                  sx={{ width: 200 }}
                />
                <EmojiField
                  label={t('admin.chatStickers.fieldEmoji')}
                  value={uploadEmoji}
                  onChange={setUploadEmoji}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {t('admin.chatStickers.fileHint')}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUploadOpen(false)} disabled={uploading}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleUpload()}
              disabled={uploading || processing || !uploadFile}
              startIcon={uploading ? <CircularProgress size={16} /> : undefined}
            >
              {uploading ? t('common.loading') : t('admin.chatStickers.addSticker')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={editSticker !== null} onClose={() => !saving && setEditSticker(null)} fullWidth maxWidth="sm">
          <DialogTitle>{t('admin.chatStickers.editStickerTitle')}</DialogTitle>
          <DialogContent>
            {editError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditError(null)}>
                {editError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {editSticker && (
                <img
                  src={editSticker.url}
                  alt={editSticker.code}
                  style={{ width: 56, height: 56, objectFit: 'contain' }}
                />
              )}
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField
                  label={t('admin.chatStickers.fieldStickerCode')}
                  value={editCode}
                  onChange={(e) => setEditCode(e.target.value)}
                  size="small"
                  sx={{ width: 200 }}
                />
                <EmojiField
                  label={t('admin.chatStickers.fieldEmoji')}
                  value={editEmoji}
                  onChange={setEditEmoji}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditSticker(null)} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleSaveEdit()}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : undefined}
            >
              {saving ? t('common.loading') : t('common.save')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}
